/* eslint-disable @typescript-eslint/no-explicit-any*/
import {
  getTokenAuthHeaders,
  LocalStorageKeys,
  ZealErrorAlias,
} from "@zeal/common";
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosRequestHeaders,
} from "axios";

const MAX_RETRIES = 3;
const RETRY_STATUS_CODES = new Set<number>([502]);

export interface IBearerTokenAuth {
  readonly type: "bearer-token";
  readonly token: string;
}

export interface ITokenAuth {
  readonly type: "token";
  readonly token: string;
}

export interface ICookieAuth {
  readonly type: "cookie";
}

export interface ILocalStorageAuth {
  readonly type: "localStorage";
}

export type ClientAuthParameters =
  | IBearerTokenAuth
  | ICookieAuth
  | ITokenAuth
  | ILocalStorageAuth;

const getAuthHeaders = (
  auth: ClientAuthParameters,
  getTokenFn?: GetTokenFn
): AxiosRequestHeaders => {
  if (auth.type === "token" || auth.type === "bearer-token") {
    return {
      Authorization: `Bearer ${auth.token}`,
    };
  }
  if (auth.type === "localStorage") {
    return getTokenAuthHeaders(getTokenFn);
  }
  return {};
};

export type GetTokenFn = (key: LocalStorageKeys) => string | null;
export type SetTokenFn = (key: LocalStorageKeys, value: string) => void;

export interface IApiClientOptions {
  readonly auth: ClientAuthParameters;
  readonly baseURL: string;
  readonly defaultRequestTimeoutMs?: number;
  readonly headers?: Record<string, string>;
  readonly onInvalidResponseType?: (message: string) => Promise<void>;
  /** Invoked when the API returns 403 with no-access-to-endpoint / route Zeal error codes. */
  readonly onNoAccessToEndpointError?: (message: string) => void;
  readonly getTokenFn?: GetTokenFn;
  readonly setTokenFn?: SetTokenFn;
}

const defaultTimeoutMS = 60000;

export interface IAxiosInstance {
  readonly noRetry: AxiosInstance;
  readonly with502Retry: AxiosInstance;
}

declare module "axios" {
  export interface AxiosRequestConfig {
    _retry?: boolean;
    _isRetryRequest?: boolean;
  }
}

type RefreshedTokens = { sessionToken: string; primaryToken?: string };

export abstract class AbstractApiClient {
  private axiosInstance: AxiosInstance;
  private axiosInstanceWith502Retry: AxiosInstance;

  private baseURL: string;
  private refreshTokenURL?: string;
  private getTokenFn?: GetTokenFn;
  private setTokenFn?: SetTokenFn;

  private isRefreshing = false;
  private requestsPendingForRefresh: {
    resolve: (value: RefreshedTokens) => void;
    reject: (reason?: unknown) => void;
  }[] = [];

  public get Instance(): IAxiosInstance {
    return {
      noRetry: this.axiosInstance,
      with502Retry: this.axiosInstanceWith502Retry,
    };
  }

  /**
   * Registers after other client setup (e.g. refresh-token interceptor) so this runs first on errors.
   */
  protected registerNoAccessToEndpointErrorHandler(
    onNoAccessToEndpointError: (message: string) => void
  ): void {
    const attach = (axiosInstance: AxiosInstance) => {
      axiosInstance.interceptors.response.use(
        (response) => response,
        (error) => {
          const status = error.response?.status;
          const errors = error.response?.data?.errors as
            | { code?: number; message?: string }[]
            | undefined;
          if (status === 403 && Array.isArray(errors)) {
            const noAccessErr = errors.find(
              (e) =>
                e.code === ZealErrorAlias.NO_ACCESS_TO_ENDPOINT.code ||
                e.code === ZealErrorAlias.NO_ACCESS_TO_ROUTE.code
            );
            if (noAccessErr?.message) {
              onNoAccessToEndpointError(noAccessErr.message);
            }
          }
          return Promise.reject(error);
        }
      );
    };
    attach(this.axiosInstance);
    attach(this.axiosInstanceWith502Retry);
  }

  public constructor(options: IApiClientOptions) {
    const shouldUseCookieAuth = options.auth.type === "cookie";
    const instanceOptions: AxiosRequestConfig = {
      headers: {
        ...(options.headers ?? {}),
        ...getAuthHeaders(options.auth, options.getTokenFn),
      },
      baseURL: options.baseURL,
      timeout: options?.defaultRequestTimeoutMs
        ? options?.defaultRequestTimeoutMs
        : defaultTimeoutMS,
      withCredentials: shouldUseCookieAuth,
    };

    this.baseURL = options.baseURL;

    this.axiosInstance = axios.create(instanceOptions);
    this.axiosInstanceWith502Retry = axios.create(instanceOptions);

    this.getTokenFn = options.getTokenFn;
    this.setTokenFn = options.setTokenFn;

    // session token
    this.configureSessionTokenInterceptor(this.axiosInstance);
    this.configureSessionTokenInterceptor(this.axiosInstanceWith502Retry);

    // retry
    this.configureRetryInterceptor(this.axiosInstanceWith502Retry);
  }

  public configureRefreshTokenInterceptor = (refreshTokenURL: string) => {
    this.refreshTokenURL = refreshTokenURL;
    this._configureRefreshTokenInterceptor(this.axiosInstance);
    this._configureRefreshTokenInterceptor(this.axiosInstanceWith502Retry);
  };

  private configureSessionTokenInterceptor = (axiosInstance: AxiosInstance) => {
    axiosInstance.interceptors.request.use((requestConfig) => {
      if (requestConfig._isRetryRequest) {
        return requestConfig;
      }

      if (
        !Object.prototype.hasOwnProperty.call(globalThis, "localStorage") &&
        !this.getTokenFn
      ) {
        return requestConfig;
      }

      const getToken =
        this.getTokenFn ?? ((key: string) => localStorage.getItem(key));

      const sessionToken = getToken(LocalStorageKeys.SESSION_TOKEN);
      const primaryToken = getToken(LocalStorageKeys.PRIMARY_TOKEN);
      const secondaryToken = getToken(LocalStorageKeys.SECONDARY_TOKEN);

      return {
        ...requestConfig,
        headers: {
          ...(requestConfig.headers || {}),
          ...(sessionToken ? { "x-session-token": sessionToken } : {}),
          ...(primaryToken ? { "x-primary-token": primaryToken } : {}),
          ...(secondaryToken ? { "x-secondary-token": secondaryToken } : {}),
        },
      };
    });
  };

  private configureRetryInterceptor = (axiosInstance: AxiosInstance) => {
    axiosInstance.interceptors.response.use(undefined, async (error) => {
      const config = error.config;

      if (!config) {
        return Promise.reject(error);
      }

      if (RETRY_STATUS_CODES.has(error.response?.status)) {
        if (config._isRetryRequest) {
          return Promise.reject(error);
        }

        if (config.retryCount >= MAX_RETRIES) {
          return Promise.reject(error);
        }

        config.retryCount = config.retryCount ? config.retryCount + 1 : 1;

        const backoff = 2 ** config.retryCount * 1000;

        await new Promise((resolve) => setTimeout(resolve, backoff));

        return this.axiosInstance(config);
      }

      // Reject if the error is not a 502
      return Promise.reject(error);
    });
  };

  private _configureRefreshTokenInterceptor = (
    axiosInstance: AxiosInstance
  ) => {
    const getToken =
      this.getTokenFn ?? ((key: string) => localStorage.getItem(key));
    const setToken =
      this.setTokenFn ??
      ((key: LocalStorageKeys, value: string) => {
        localStorage.setItem(key, value);
      });

    axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (originalRequest._isRetryRequest) {
          return Promise.reject(error);
        }

        if (
          error.response?.status === ZealErrorAlias.INVALID_JWT.status &&
          error.response.data?.errors?.find(
            (e: any) => e.code === ZealErrorAlias.INVALID_JWT.code
          )
        ) {
          const refreshToken = getToken(LocalStorageKeys.REFRESH_TOKEN);
          const primaryToken = getToken(LocalStorageKeys.PRIMARY_TOKEN);
          if (!refreshToken || this.refreshTokenURL === undefined) {
            return Promise.reject(error);
          }

          const newTokensAvailablePromise = new Promise<RefreshedTokens>(
            (resolve, reject) => {
              this.requestsPendingForRefresh.push({ resolve, reject });
            }
          );

          if (!this.isRefreshing) {
            this.isRefreshing = true;
            try {
              const { data: resData, status } = await axios.post(
                `${this.baseURL}${this.refreshTokenURL}`,
                {
                  refreshToken,
                  primaryToken,
                },
                {
                  _isRetryRequest: true,
                }
              );
              const {
                sessionToken: newSessionToken,
                refreshToken: newRefreshToken,
                primaryToken: newPrimaryToken,
              } = resData.data;

              if (status !== 200 || !newSessionToken || !newRefreshToken) {
                this.requestsPendingForRefresh.forEach((promise) =>
                  promise.reject(error)
                );
                this.requestsPendingForRefresh = [];
                setToken(LocalStorageKeys.SESSION_TOKEN, "");
                setToken(LocalStorageKeys.REFRESH_TOKEN, "");
                setToken(LocalStorageKeys.PRIMARY_TOKEN, "");
                return Promise.reject(error);
              }

              setToken(LocalStorageKeys.REFRESH_TOKEN, newRefreshToken);
              setToken(LocalStorageKeys.SESSION_TOKEN, newSessionToken);
              setToken(LocalStorageKeys.PRIMARY_TOKEN, newPrimaryToken);

              this.requestsPendingForRefresh.forEach((promise) =>
                promise.resolve({
                  sessionToken: newSessionToken,
                  primaryToken: newPrimaryToken,
                })
              );
              this.requestsPendingForRefresh = [];

              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers["x-session-token"] = newSessionToken;
              if (newPrimaryToken)
                originalRequest.headers["x-primary-token"] = newPrimaryToken;
              originalRequest._isRetryRequest = true;

              return axiosInstance(originalRequest);
            } catch (refreshError) {
              this.requestsPendingForRefresh.forEach((promise) =>
                promise.reject(refreshError)
              );
              this.requestsPendingForRefresh = [];
              setToken(LocalStorageKeys.SESSION_TOKEN, "");
              setToken(LocalStorageKeys.REFRESH_TOKEN, "");
              setToken(LocalStorageKeys.PRIMARY_TOKEN, "");
              return Promise.reject(refreshError);
            } finally {
              this.isRefreshing = false;
            }
          } else {
            return newTokensAvailablePromise.then((tokens: RefreshedTokens) => {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers["x-session-token"] = tokens.sessionToken;
              if (tokens.primaryToken)
                originalRequest.headers["x-primary-token"] =
                  tokens.primaryToken;
              originalRequest._isRetryRequest = true;
              return axiosInstance(originalRequest);
            });
          }
        }
        return Promise.reject(error);
      }
    );
  };
}
